/**
 * CommandPalette (⌘K), one fuzzy launcher over every navigation target, the key
 * RFCs, the sample exhibits, and the chrome actions. Keyboard-first: type to
 * filter, ↑/↓ to move, ↵ to run, Esc to close. Listbox a11y throughout.
 *
 * Results render bucketed by group with header rows (square pip + label);
 * the keyboard index stays FLAT across buckets, so arrows walk the visible
 * order and headers are skipped by construction. With an empty query a
 * Recent bucket (this session's last-run commands, newest first) leads.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";
import { STORAGE_KEYS } from "../utils/storageKeys";
import { useT, type Locale } from "./i18n";
import { useModalFocus } from "./useModalFocus";

export interface Command {
  id: string;
  label: string;
  group: string;
  hint?: string;
  keywords?: string;
  run: () => void;
}

/** sessionStorage key for the per-session Recent bucket (command ids). */
const RECENT_KEY = STORAGE_KEYS.cmdkRecent;
const RECENT_MAX = 6;
/** Exit-animation length; mirrors the toast leaving transition. */
const EXIT_MS = 160;

export function readRecentCommandIds(): string[] {
  try {
    const raw = sessionStorage.getItem(RECENT_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === "string") : [];
  } catch {
    return [];
  }
}

function rememberCommand(id: string): void {
  try {
    const next = [id, ...readRecentCommandIds().filter((x) => x !== id)].slice(
      0,
      RECENT_MAX,
    );
    sessionStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    // Session storage unavailable: recents simply do not persist.
  }
}

/** Palette chrome, per locale: only the shell (aria, placeholder, footer)
 *  lives here. The commands are built with t() in AppNext (cmd.* / nav.* keys),
 *  so groups, labels and hints arrive localized, with the English terms kept
 *  as keywords so the fuzzy search matches both idioms. RFC titles and sample
 *  exhibit names stay English by design: forensic vocabulary. */
const COPY: Record<
  Locale,
  {
    aria: string;
    placeholder: string;
    noMatches: string;
    move: string;
    open: string;
    close: string;
    recent: string;
  }
> = {
  en: {
    aria: "Command palette",
    placeholder: "Search commands, RFCs, samples…",
    noMatches: "No matches",
    move: "move",
    open: "open",
    close: "close",
    recent: "Recent",
  },
  de: {
    aria: "Befehlspalette",
    placeholder: "Befehle, RFCs, Beispiele suchen…",
    noMatches: "Keine Treffer",
    move: "bewegen",
    open: "öffnen",
    close: "schließen",
    recent: "Zuletzt",
  },
};

/** Contiguous match ranks above a subsequence match; both beat no match (-1). */
function score(cmd: Command, q: string): number {
  if (!q) return 0;
  const hay = `${cmd.label} ${cmd.group} ${cmd.keywords ?? ""}`.toLowerCase();
  const needle = q.toLowerCase().trim();
  const idx = hay.indexOf(needle);
  if (idx >= 0) return 1000 - idx;
  let hi = 0;
  let qi = 0;
  for (; hi < hay.length && qi < needle.length; hi++) {
    if (hay[hi] === needle[qi]) qi++;
  }
  return qi === needle.length ? 100 : -1;
}

/** One rendered row: a bucket header, or a command carrying its flat index. */
type PaletteRow =
  | { kind: "header"; label: string }
  | { kind: "cmd"; cmd: Command; index: number };

export function CommandPalette({
  commands,
  onClose,
}: {
  commands: Command[];
  onClose: () => void;
}) {
  const { locale } = useT();
  const c = COPY[locale] ?? COPY.en;
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(0);
  const [leaving, setLeaving] = useState(false);
  const listRef = useRef<HTMLUListElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Exit mirrors the toast leaving pattern: a short class-driven fade, then
  // the real close. Reduced motion collapses the transition via the global
  // kill, so the delay is imperceptible there.
  const close = useCallback(() => {
    setLeaving(true);
    window.setTimeout(onClose, EXIT_MS);
  }, [onClose]);
  useModalFocus(panelRef, { onClose: close });

  const { rows, flat } = useMemo(() => {
    const scored = commands.map((c) => ({ c, s: score(c, q) })).filter((x) => x.s > -1);
    scored.sort((a, b) => b.s - a.s);
    const top = scored.slice(0, 50).map((x) => x.c);
    // Bucket by group, ordered by each group's best-scoring member (i.e.
    // first appearance in the score order).
    const buckets = new Map<string, Command[]>();
    if (!q.trim()) {
      const byId = new Map(commands.map((cmd) => [cmd.id, cmd]));
      const recents = readRecentCommandIds()
        .map((id) => byId.get(id))
        .filter((cmd): cmd is Command => !!cmd);
      if (recents.length > 0) buckets.set(c.recent, recents);
    }
    for (const cmd of top) {
      const bucket = buckets.get(cmd.group) ?? [];
      bucket.push(cmd);
      buckets.set(cmd.group, bucket);
    }
    const outRows: PaletteRow[] = [];
    const outFlat: Command[] = [];
    for (const [group, cmds] of buckets) {
      outRows.push({ kind: "header", label: group });
      for (const cmd of cmds) {
        outRows.push({ kind: "cmd", cmd, index: outFlat.length });
        outFlat.push(cmd);
      }
    }
    return { rows: outRows, flat: outFlat };
  }, [commands, q, c.recent]);

  useEffect(() => {
    setSel(0);
  }, [q]);

  useEffect(() => {
    listRef.current
      ?.querySelector(`[data-i="${sel}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [sel]);

  const run = (i: number) => {
    const cmd = flat[i];
    if (cmd) {
      rememberCommand(cmd.id);
      close();
      cmd.run();
    }
  };

  const onKey = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSel((s) => Math.min(s + 1, Math.max(flat.length - 1, 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSel((s) => Math.max(s - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      run(sel);
    }
  };

  return (
    <div
      className={`nx-cmdk${leaving ? " nx-cmdk--leaving" : ""}`}
      role="dialog"
      aria-modal="true"
      aria-label={c.aria}
      onMouseDown={close}
    >
      <div
        className="nx-cmdk__panel"
        ref={panelRef}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="nx-cmdk__inputrow">
          <input
            className="nx-cmdk__input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKey}
            placeholder={c.placeholder}
            aria-label={c.aria}
            spellCheck={false}
            autoComplete="off"
            role="combobox"
            aria-expanded="true"
            aria-controls="nx-cmdk-list"
            aria-activedescendant={flat.length ? `nx-cmd-${sel}` : undefined}
          />
          <button
            type="button"
            className="nx-cmdk__close"
            onClick={close}
            aria-label={c.close}
          >
            ✕
          </button>
        </div>
        {/* tabIndex -1: the combobox input owns keyboard interaction via
            aria-activedescendant; without it Chrome adds the scrollable list
            itself to the tab order. */}
        <ul
          className="nx-cmdk__list"
          id="nx-cmdk-list"
          role="listbox"
          ref={listRef}
          tabIndex={-1}
        >
          {rows.map((row, r) =>
            row.kind === "header" ? (
              <li
                key={`h-${row.label}-${r}`}
                className="nx-cmdk__bucket"
                role="presentation"
              >
                <span className="nx-cmdk__bucketpip" aria-hidden="true" />
                {row.label}
              </li>
            ) : (
              <li
                key={`${row.cmd.id}-${row.index}`}
                id={`nx-cmd-${row.index}`}
                data-i={row.index}
                role="option"
                aria-selected={row.index === sel}
                className={`nx-cmdk__item${row.index === sel ? " is-sel" : ""}`}
                onMouseEnter={() => setSel(row.index)}
                onClick={() => run(row.index)}
              >
                <span className="nx-cmdk__label">{row.cmd.label}</span>
                {row.cmd.hint ? (
                  <span className="nx-cmdk__hint">{row.cmd.hint}</span>
                ) : null}
              </li>
            ),
          )}
        </ul>
        {/* "No matches" sits OUTSIDE the listbox. A role="listbox" whose only
            child is a role="presentation" row fails aria-required-children
            (critical): an empty listbox is valid, a listbox of non-options is
            not. role="status" also means the message is announced when a
            query stops matching, rather than passing silently. */}
        {flat.length === 0 ? (
          <p className="nx-cmdk__empty" role="status">
            {c.noMatches}
          </p>
        ) : null}
        <div className="nx-cmdk__foot">
          <span>↑↓ {c.move}</span>
          <span>↵ {c.open}</span>
          <span>esc {c.close}</span>
        </div>
      </div>
    </div>
  );
}
